import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { format } from 'date-fns';
import { ReactNode, useCallback, useId, useMemo } from 'react';
import { Controller, useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { logError } from 'utils/monitoring';

// Hooks
import { useModal } from 'components/modals/useModal';

// Util
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';

// Components
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import FileInput from 'components/forms/inputs/rhf-inputs/FileInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { TimeInput } from 'components/forms/inputs/rhf-inputs/TimeInput';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';

// Store
import { useUploadSentInvoiceMutation } from 'store/api/sentInvoices';
import { RootState } from 'store/reducers';
import { DIALOG_560 } from '../dialogWidths';
import { FileTextIcon, UploadIcon, XIcon } from 'components/icons';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { FieldError } from 'components/ui/field';

interface AddSentInvoiceModalProps {
    onClose: () => void;
    invoiceId: number;
    invoiceNumber: string;
}

interface AddSentInvoiceForm {
    invoicePdf: File[];
    sentDate: string;
    sentTime: Date | null;
    invoiceStatusTypeCode?: { value: string; label: string } | null;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/** "184 KB", or "1.2 MB" from a megabyte up. */
const fileSize = (bytes: number) =>
    bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** The new design's PDF field: a drop zone that also opens the picker, and the chosen file as a row under it. */
function SentPdfDropZone({ control, setValue }: Pick<UseFormReturn<AddSentInvoiceForm>, 'control' | 'setValue'>) {
    const labelId = useId();
    const errorId = useId();
    const choose = (files: FileList | null) => {
        if (files?.[0]) {
            setValue('invoicePdf', [files[0]], { shouldValidate: true });
        }
    };
    return (
        <Controller
            control={control}
            name="invoicePdf"
            render={({ field: { value, ref }, fieldState: { error } }) => {
                const [file] = value ?? [];
                return (
                    <div className="flex flex-col gap-1.5">
                        <span id={labelId} className="text-foreground-secondary text-sm">
                            {t('invoices.upload-sent-invoice')}
                            <span className="text-danger-ink">&thinsp;*</span>
                        </span>
                        <label
                            className="border-input bg-surface-tile has-[:focus-visible]:outline-primary flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border border-dashed px-4 py-6 text-center has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2"
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                                event.preventDefault();
                                choose(event.dataTransfer.files);
                            }}
                        >
                            <span
                                aria-hidden
                                className="bg-card text-foreground-secondary grid size-10 place-items-center rounded-full shadow-xs"
                            >
                                <UploadIcon className="size-5" />
                            </span>
                            <span className="text-foreground text-sm">
                                <FormattedMessage
                                    id="invoices.sent-invoice-drop"
                                    values={{
                                        link: (chunks: ReactNode) => (
                                            <span className="text-primary font-medium underline">{chunks}</span>
                                        ),
                                    }}
                                />
                            </span>
                            <span className="text-caption text-foreground-tertiary">
                                {t('invoices.sent-invoice-drop-hint')}
                            </span>
                            <input
                                ref={ref}
                                type="file"
                                accept="application/pdf"
                                className="sr-only"
                                aria-labelledby={labelId}
                                aria-invalid={!!error}
                                aria-describedby={error ? errorId : undefined}
                                onChange={(event) => {
                                    choose(event.target.files);
                                    event.target.value = '';
                                }}
                            />
                        </label>
                        {file ? (
                            <div className="border-input flex items-center gap-3 rounded-lg border px-3 py-2 shadow-xs">
                                <span
                                    aria-hidden
                                    className="bg-muted text-foreground-secondary grid size-8 shrink-0 place-items-center rounded-md"
                                >
                                    <FileTextIcon className="size-4" />
                                </span>
                                <span className="flex min-w-0 flex-auto flex-col">
                                    <span className="text-body1 text-foreground truncate font-medium">{file.name}</span>
                                    <span className="text-caption text-foreground-tertiary">
                                        {`${t('invoices.pdf-link')} · ${fileSize(file.size)}`}
                                    </span>
                                </span>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    className="text-foreground-tertiary"
                                    aria-label={`${t('remove')} ${file.name}`}
                                    onClick={() => setValue('invoicePdf', [], { shouldValidate: true })}
                                >
                                    <XIcon />
                                </Button>
                            </div>
                        ) : null}
                        <FieldError id={errorId} role={undefined} errors={error ? [error] : undefined} />
                    </div>
                );
            }}
        />
    );
}

const AddSentInvoiceModal = (props: AddSentInvoiceModalProps) => {
    const { onClose, invoiceId, invoiceNumber } = props;
    const isNew = useIsNewDesign();
    const buttonsDisabled = useSelector((state: RootState) => state.buttons.buttonsDisabled);
    const [uploadSentInvoice] = useUploadSentInvoiceMutation();

    const { handleSubmit, control, setError, setValue } = useForm<AddSentInvoiceForm>({
        defaultValues: {
            invoicePdf: [],
            sentDate: format(new Date(), 'yyyy-MM-dd'),
            sentTime: new Date(),
            invoiceStatusTypeCode: null,
        },
    });

    const [invoicePdf, sentDate, sentTime, invoiceStatusTypeCode] = useWatch({
        control,
        name: ['invoicePdf', 'sentDate', 'sentTime', 'invoiceStatusTypeCode'],
    });

    const isUploadDisabled =
        !invoicePdf || invoicePdf.length === 0 || !sentDate || !sentTime || !invoiceStatusTypeCode?.value;

    const statusOptions = useMemo(
        () => [
            { value: 'SENT', label: t('invoices.status-sent') },
            { value: 'FINALIZED', label: t('invoices.status-finalized') },
        ],
        []
    );

    const onSubmit = useCallback(
        async (data: AddSentInvoiceForm) => {
            const file = data.invoicePdf[0];

            // Validate file
            if (!file) {
                errorAlert(t('invoices.sent-invoice-no-file'));
                return;
            }

            if (file.type !== 'application/pdf') {
                setError('invoicePdf', {
                    type: 'manual',
                    message: t('invoices.sent-invoice-invalid-type'),
                });
                errorAlert(t('invoices.sent-invoice-invalid-type'));
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setError('invoicePdf', {
                    type: 'manual',
                    message: t('invoices.sent-invoice-file-too-large'),
                });
                errorAlert(t('invoices.sent-invoice-file-too-large'));
                return;
            }

            try {
                // Combine the selected date (yyyy-MM-dd) and time (local Date) in the user's
                // local timezone, then convert to UTC for the backend.
                const [year, month, day] = data.sentDate.split('-').map(Number);
                const selectedTime = data.sentTime ?? new Date();
                const combined = new Date(
                    year,
                    month - 1,
                    day,
                    selectedTime.getHours(),
                    selectedTime.getMinutes(),
                    selectedTime.getSeconds(),
                    0
                );
                const sentDateTime = combined.toISOString();

                await uploadSentInvoice({
                    invoiceId,
                    file,
                    sentDateTime,
                    invoiceStatusTypeCode: data.invoiceStatusTypeCode?.value,
                }).unwrap();

                successAlert(t('invoices.sent-invoice-uploaded-success'));
                onClose();
            } catch (error) {
                console.error('Failed to upload sent invoice:', error);
                errorAlert(t('invoices.sent-invoice-upload-error'));
                logError(error);
            }
        },
        [uploadSentInvoice, invoiceId, onClose, setError]
    );

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.add-sent-invoice-history')}
                description={`${t('invoices.invoice-number')}: ${invoiceNumber}`}
                onCancel={onClose}
            />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                <div className="flex flex-col gap-6">
                    {/* The new design names the invoice in the header's description. */}
                    <div className="new:hidden">
                        <strong>{t('invoices.invoice-number')}:</strong> {invoiceNumber}
                    </div>

                    {isNew ? (
                        <SentPdfDropZone control={control} setValue={setValue} />
                    ) : (
                        <FileInput<AddSentInvoiceForm>
                            name="invoicePdf"
                            control={control}
                            setValue={setValue}
                            label="invoices.upload-sent-invoice"
                            accept="application/pdf"
                            required
                            maxFiles={1}
                        />
                    )}

                    {/* The new design sets the date and the time side by side, each filling its half. */}
                    <div className="new:grid new:grid-cols-2 new:gap-4 contents">
                        <DateInput<AddSentInvoiceForm>
                            control={control}
                            label="invoices.sent-date"
                            name="sentDate"
                            required
                            maxDate={new Date()}
                            fullWidth={isNew}
                        />

                        <TimeInput<AddSentInvoiceForm>
                            control={control}
                            setValue={setValue}
                            name="sentTime"
                            label="invoices.sent-time"
                            required
                            timeHasErrors={false}
                            fullWidth={isNew}
                        />
                    </div>

                    <div className="pt-2">
                        <SelectInput<AddSentInvoiceForm, { value: string; label: string }>
                            control={control}
                            setValue={setValue}
                            name="invoiceStatusTypeCode"
                            label="invoices.status"
                            options={statusOptions}
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            required
                            clearable={isNew ? false : undefined}
                        />
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                kit
                buttons={[
                    <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                        {t('cancel')}
                    </Button>,
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        key="upload"
                        onClick={() => {
                            void handleSubmit(onSubmit)();
                        }}
                        disabled={buttonsDisabled || isUploadDisabled}
                    >
                        {t('upload')}
                    </Button>,
                ]}
            />
        </>
    );
};

export const useAddSentInvoiceModal = (invoiceId: number, invoiceNumber: string) => {
    const { openModal, closeModal } = useModal();

    const openAddSentInvoiceModal = useCallback(() => {
        openModal(
            <AddSentInvoiceModal onClose={closeModal} invoiceId={invoiceId} invoiceNumber={invoiceNumber} />,
            closeModal,
            undefined,
            undefined,
            DIALOG_560
        );
    }, [invoiceId, invoiceNumber, openModal, closeModal]);

    return {
        openAddSentInvoiceModal,
    };
};
